import * as React from 'react';
import { XAxis, CartesianGrid, Area, ContentRenderer, Dot, LineType, Margin, YAxis, ComposedChart } from 'recharts';
import { CONSTANTS } from '../../../assets/UIConstants';
import * as moment from 'moment';
import { computed } from 'mobx';
import { Comparision } from '../../pages/Public';

export const GRID_ORANGE = '#FFA129';
export const GRID_BLUE = '#4D7EFF';

interface IDataPoint {
    x: number;
    y: number;
}

type SupportedIntervals = 'weeks' | 'months';

export interface NumericRange {
    min: number;
    max: number;
}

interface Props {
    dataPoints: IDataPoint[];
    width?: number;
    height?: number;
    hasGrid?: boolean;
    accentColor?: string;
    dotRadius?: number;
    lineWidth?: number;
    type?: LineType;
    margin?: Partial<Margin>;
    tickStyle?: React.CSSProperties;
    hasSmallFinalDot?: boolean;
    xAxisTickFormatter?: (value: number) => string;
    interval?: SupportedIntervals;
    yAxisRange?: NumericRange;
    showYAxis?: boolean;
    yAxisTickUnit?: string;
    goodValueComparisionValue?: number;
    goodValueComparision?: Comparision;
}


const blueColor: string = CONSTANTS.COLORS.THEME_BLUE;
const greyColor = CONSTANTS.COLORS.GRAPH_GREY;
const tickColor = CONSTANTS.COLORS.THEME_GREY;
const DEFAULT_WIDTH = 300;
const DEFAULT_X_AXIS_INTERVAL = 'weeks';


export class AreaGraph extends React.Component<Props> {

    public shouldRemoveDotOnX: number | undefined;

    public static defaultProps: Partial<Props> = {
        hasGrid: true,
        accentColor: blueColor,
        lineWidth: 2,
        dotRadius: 8
    };

    protected renderDot: ContentRenderer<any> = (props: any) => {
        const key = props.key;
        props.key = undefined;
        props.radius = this.props.dotRadius;

        if (this.props.dataPoints.length - 1 === props.index) {
            return (
                <g key={key}>
                    <Dot
                        {...props}
                        fill={'transparent'}
                        r={this.props.dotRadius! * (this.props.hasSmallFinalDot ? 1.55 : 3.15)}
                        strokeWidth={5}
                        strokeOpacity={0.2}
                        dx={1}
                    />
                    {this.dataPoint(props, this.shouldRemoveDotOnX)}
                </g>
            );
        } else {
            return (
                <g key={key}>
                    {this.dataPoint(props, this.shouldRemoveDotOnX)}
                </g>
            );
        }
    }

    protected generateYAxisTicks(): number[] {
        console.assert(this.props.yAxisRange != undefined, 'yAxis range must be defined to generate yAxis ticks');
        if (this.props.yAxisRange == undefined) {
            return [];
        }

        const min = this.props.yAxisRange.min;
        const max = this.props.yAxisRange.max;
        const yRange = max - min;

        const amountOfTicks = 5;
        const distanceBetween = Math.ceil(yRange / amountOfTicks);

        const ticks = [];

        for (var i = 1; i <= amountOfTicks; i++) {
            ticks.push(distanceBetween * i);
        }

        return ticks;
    }

    protected generateDateTicks(): number[] {
        let minDate: IDataPoint = this.props.dataPoints[0];
        let maxDate: IDataPoint = this.props.dataPoints[0];
        this.props.dataPoints.forEach((entry) => {
            if (entry.x < minDate.x) {
                minDate = entry;
            }
            if (entry.x > maxDate.x) {
                maxDate = entry;
            }
        });
        const out: number[] = [];
        const tmp = moment(minDate.x);
        out.push(tmp.valueOf());
        while (tmp.valueOf() < maxDate.x) {
            tmp.add(1, this.props.interval || DEFAULT_X_AXIS_INTERVAL);
            out.push(tmp.valueOf());
        }
        out.sort((a, b) => {
            if (a > b) {
                return 1;
            } else if (a < b) {
                return -1;
            }
            return 0;
        });
        return out;
    }

    protected generateDomain(): number[] {
        // needs to return the domain of our axes, which is not the same as the domain of our data.
        // we may want to render the axes for a 6 week period, but only have data for a 2 week period.
        const ticks: number[] = this.generateDateTicks();
        let min: number = ticks[0];
        let max: number = ticks[0];
        ticks.map((entry) => {
            if (entry > max) {
                max = entry;
            }
            if (entry < min) {
                min = entry;
            }
        });

        return [min, max];
    }

    public render(): JSX.Element {
        return this.renderGraph();
    }

    @computed private get sortedDataPoints(): IDataPoint[] {
        let dataPoints = this.checkForSingleDataPointAndReformat(this.props.dataPoints);

        return dataPoints.sort((a, b) => {
            return ((a.x > b.x) ? 1 : -1);
        });
    }

    private checkForSingleDataPointAndReformat(data: IDataPoint[]): IDataPoint[] {
        const dataPoints = data;

        if (dataPoints.length === 1) {
            const newDataPoint = { ...dataPoints[0] };
            const threeWeeksInMS = 2.1 * 7 * 24 * 60 * 60 * 1000;
            newDataPoint.x -= threeWeeksInMS;
            this.shouldRemoveDotOnX = newDataPoint.x;
            dataPoints.push(newDataPoint);
        } else {
            this.shouldRemoveDotOnX = undefined;
        }

        return dataPoints;
    }

    public checkIfDotIsBlue(yPoint: number) {
        let isBlue = true;
        if (this.props.goodValueComparision && this.props.goodValueComparisionValue) {
            if (this.props.goodValueComparision === Comparision.GREATER_THAN) {
                isBlue = yPoint > this.props.goodValueComparisionValue!;
            }

            if (this.props.goodValueComparision === Comparision.LESS_THAN) {
                isBlue = yPoint < this.props.goodValueComparisionValue!;
            }

            if (yPoint === this.props.goodValueComparisionValue) {
                isBlue = true;
            }
        } else {
            isBlue = false;
        }

        return !isBlue;
    }

    private findIfLastDataPointIsBlue(dataPoints: IDataPoint[]) {
        if (dataPoints.length === 0 || this.props.goodValueComparisionValue == undefined || this.props.goodValueComparision == undefined) {
            return true;
        }

        const lastPoint = dataPoints[dataPoints.length - 1];
        const isBlue = this.checkIfDotIsBlue(lastPoint.y);

        return isBlue;
    }

    public renderGraph(): JSX.Element {
        const isBlue = this.findIfLastDataPointIsBlue(this.sortedDataPoints);
        const areaGradientId: string = 'areaGradient' + (isBlue ? GRID_BLUE : GRID_ORANGE);

        return (
            <ComposedChart
                width={this.props.width || DEFAULT_WIDTH}
                height={this.props.height || DEFAULT_WIDTH}
                data={this.sortedDataPoints}
                margin={this.props.margin || { top: 25, right: 40, left: 30, bottom: 5 }}
            >
                <defs>
                    <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={isBlue ? GRID_BLUE : GRID_ORANGE} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={isBlue ? GRID_BLUE : GRID_ORANGE} stopOpacity={0} />
                    </linearGradient>
                </defs>



                {this.props.hasGrid &&
                    <XAxis
                        tick={{ fill: tickColor, fontSize: 14 }}
                        dataKey="x"
                        tickFormatter={(value: any) => {
                            if (this.props.xAxisTickFormatter) {
                                return this.props.xAxisTickFormatter(value);
                            }
                            return value;
                        }}
                        tickLine={false}
                        axisLine={{ stroke: greyColor, strokeWidth: 2 }}
                        ticks={this.generateDateTicks()}
                        domain={this.generateDomain()}
                        type="number"
                    />}

                {this.props.hasGrid &&
                    <CartesianGrid
                        stroke={greyColor}
                        strokeWidth={2}
                        strokeOpacity={1}
                        horizontal={false}
                    />
                }

                {this.props.yAxisRange &&
                    <YAxis
                        hide={!this.props.showYAxis}
                        domain={[this.props.yAxisRange.min, this.props.yAxisRange.max]}
                        axisLine={{ stroke: greyColor, strokeWidth: 2 }}
                        tick={{ fill: tickColor, fontSize: 14 }}
                        tickLine={{ stroke: tickColor, strokeWidth: 1 }}
                        tickFormatter={(value: any) => {
                            if (value === 0) {
                                return '';
                            }
                            return value + (this.props.yAxisTickUnit || '');
                        }}
                        tickSize={10}
                        ticks={this.generateYAxisTicks()}
                    />
                }

                <Area
                    type={this.props.type || 'linear'}
                    dataKey="y"
                    stroke={isBlue ? GRID_BLUE : GRID_ORANGE}
                    strokeWidth={this.props.lineWidth}
                    yAxisId={0}
                    dot={this.renderDot}
                    fill={`url(#${areaGradientId})`}
                    animationDuration={100}
                />

            </ComposedChart>
        );
    }

    dataPoint(props: any, shouldRemoveAt: number | undefined): React.ReactNode {
        if (props.payload.x === shouldRemoveAt) {
            return null;
        }

        const isBlue = this.checkIfDotIsBlue(props.payload.y);

        return (
            <Dot
                {...props}
                fill={'white'}
                stroke={isBlue ? GRID_BLUE : GRID_ORANGE}
                fillOpacity={1}
                r={props.radius}
                strokeWidth={props.radius - 1}
                strokeOpacity={1}
            />
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/components/Graphs/AreaGraph.tsx